const normalizeText = (value = "") =>
  value.normalize("NFKC").trim().toLowerCase().replace(/\s+/g, " ");

// Only normalize presentation, not dose amounts, units, or release formulations.
const normalizeDose = (value) =>
  normalizeText(value).replace(/(\d)\s+(mg|mcg|g|ml|l|iu|units?)\b/g, "$1$2");

const normalizeFrequency = (value) => {
  const normalized = normalizeText(value);
  return ["once daily", "once a day", "once per day", "every day"].includes(
    normalized,
  )
    ? "daily"
    : normalized;
};

function matchingMedication(a, b) {
  if (!a.name?.trim() || !b.name?.trim()) return false;
  // Separate saved records/restart episodes must keep their own history IDs.
  const aRecord = a.id || a.restartOfID;
  const bRecord = b.id || b.restartOfID;
  if (aRecord && bRecord && aRecord !== bRecord) return false;
  if (a.taking !== b.taking) return false;
  if (normalizeText(a.name) !== normalizeText(b.name)) return false;
  if (normalizeDose(a.dose) !== normalizeDose(b.dose)) return false;
  if (normalizeFrequency(a.frequency) !== normalizeFrequency(b.frequency))
    return false;
  if (normalizeDose(a.productStrength) !== normalizeDose(b.productStrength))
    return false;
  if (normalizeText(a.form) !== normalizeText(b.form)) return false;
  if (normalizeText(a.route) !== normalizeText(b.route)) return false;
  // Missing catalog matches can vary with availability; conflicting known
  // identifiers, however, are not evidence that these are the same product.
  return ["rxCUI", "ndc"].every(
    (field) => !a[field] || !b[field] || a[field] === b[field],
  );
}

export function deduplicateMedicationEntries(entries = []) {
  const unique = [];
  for (const entry of entries) {
    const index = unique.findIndex((existing) =>
      matchingMedication(existing, entry),
    );
    if (index === -1) {
      unique.push(entry);
      continue;
    }
    const existing = unique[index];
    // Prefer the saved record over a matching unsaved suggestion. Preserve the
    // patient's displayed values and never invent details to make two rows match.
    const priority = (item) => (item.id ? 2 : item.restartOfID ? 1 : 0);
    const retained = priority(entry) > priority(existing) ? entry : existing;
    const other = retained === entry ? existing : entry;
    unique[index] = {
      ...retained,
      rxCUI: retained.rxCUI || other.rxCUI || "",
      ndc: retained.ndc || other.ndc || "",
      ...(existing.previouslyReported || entry.previouslyReported
        ? { previouslyReported: true }
        : {}),
    };
  }
  return unique;
}

export function mergeMedicationEntries(existing, incoming) {
  const previous = deduplicateMedicationEntries(existing);
  const entries = deduplicateMedicationEntries([...previous, ...incoming]);
  const addedCount = entries.length - previous.length;
  return { entries, addedCount, skippedCount: incoming.length - addedCount };
}
